import { ApolloError, gql as shopifyGQL, useQuery } from '@apollo/client'
import { paths } from '@/constants/paths'
import { getShopifyClient } from '@/services/ApolloClient/ApolloClient'
import { createShopifyClient } from '@/services/Shopify/ShopifyClient'
import { GetShopifyCollection, GetShopifyProduct } from '@/services/Shopify/queries'
import {
  GetShopifyCollectionQuery,
  GetShopifyCollectionQueryVariables,
  GetShopifyProductByIdQuery,
  GetShopifyProductByIdQueryVariables,
  Product,
} from '@/types/codegen-shopify'
import { logger } from '@/utils/logging'

interface UseCollectionResponse {
  data?: {
    collection: {
      id: string
      handle: string
      title: string
      description: string
      updatedAt: string
      products: {
        edges: [
          {
            node: ShopifyProduct
          },
        ]
      }
    }
  }
  loading: boolean
  error?: ApolloError
}

export interface ShopifyProduct {
  id: string
  title: string
  description: string
  availableForSale: boolean
  compareAtPriceRange: {
    maxVariantPrice: {
      amount: number
      currencyCode: string
    }
    minVariantPrice: {
      amount: number
      currencyCode: string
    }
  }
  priceRange: {
    maxVariantPrice: {
      amount: number
      currencyCode: string
    }
    minVariantPrice: {
      amount: number
      currencyCode: string
    }
  }
  featuredImage: {
    width: number
    height: number
    url: string
  }
  onlineStoreUrl: string
}

const collectionByHandleQuery = shopifyGQL`
  query getCollectionByHandle($handle: String!) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      updatedAt
      products(first: 150) {
        edges {
          node {
            id
            title
            description
            availableForSale
            compareAtPriceRange {
              maxVariantPrice {
                amount
                currencyCode
              }
              minVariantPrice {
                amount
                currencyCode
              }
            }
            priceRange {
              maxVariantPrice {
                amount
                currencyCode
              }
              minVariantPrice {
                amount
                currencyCode
              }
            }
            productType
            tags
            featuredImage {
              height
              width
              altText
              url
            }
            onlineStoreUrl
          }
        }
      }
    }
  }
`

const updatedCollectionByHandleQuery = shopifyGQL`
query getCollection($handle: String!) {
  collection: collectionByHandle(handle: $handle) {
    id
    handle
    title
    description
    updatedAt
    products(first: 150, sortKey: MANUAL, reverse:false) {
      edges {
        node {
          id
          title
          description
          compareAtPriceRange {
            maxVariantPrice {
              amount
              currencyCode
            }
            minVariantPrice {
              amount
              currencyCode
            }
          }
          priceRange {
            maxVariantPrice {
              amount
              currencyCode
            }
            minVariantPrice {
              amount
              currencyCode
            }
          }
          productType
          tags
          featuredImage {
            height
            width
            altText
            url
          }
          onlineStoreUrl
        }
      }
    }
  }
}
`

interface useCollectionProps {
  handle: string
  storeUrl: string
  accessToken: string
}

export function useShopifyCollection({ handle, storeUrl, accessToken }: useCollectionProps): UseCollectionResponse {
  const shopifyClient = createShopifyClient({ baseUrl: storeUrl, accessToken })
  const shopifyQuery = storeUrl?.includes('2024-04') ? updatedCollectionByHandleQuery : collectionByHandleQuery

  const result = useQuery(shopifyQuery, {
    variables: {
      handle: handle,
    },
    client: shopifyClient,
    errorPolicy: 'all',
  })

  return result
}

export interface ShopifyProductWithBundles extends Product {
  bundledProducts: { quantity: number; product: Product }[]
}
export async function getShopifyCollection({ collectionId }: { collectionId: string }) {
  const shopifyClient = getShopifyClient()
  const { data } = await shopifyClient.query<GetShopifyCollectionQuery, GetShopifyCollectionQueryVariables>({
    query: GetShopifyCollection,
    variables: {
      id: collectionId,
    },
    fetchPolicy: 'network-only',
  })
  const products = data?.collection?.products?.edges?.map((edge) => edge.node)

  return products?.map((product) => {
    const bundledProducts = product?.variants?.edges?.flatMap((edge) => {
      return edge?.node?.components?.edges?.flatMap((e) => {
        return { quantity: e?.node?.quantity, product: e?.node?.productVariant?.product }
      })
    })
    return { ...product, bundledProducts: bundledProducts ?? [] }
  })
}

export async function getShopifyProduct({ productId }: { productId: string }) {
  const shopifyClient = getShopifyClient()
  const { data } = await shopifyClient.query<GetShopifyProductByIdQuery, GetShopifyProductByIdQueryVariables>({
    query: GetShopifyProduct,
    variables: {
      id: productId,
    },
    fetchPolicy: 'network-only',
  })

  return data?.product
}

export async function getShopifyProductVariantId(productId: string) {
  const product = await getShopifyProduct({ productId })

  return product?.variants?.edges?.[0]?.node?.id
}

export interface MerchDiscount {
  code: string
  value: number
}

export interface Utm {
  campaign: string
  linkContext: string
}

export const buildUtmContext = (utm: Utm | undefined) => {
  return utm ? utm.linkContext : 'generic'
}

export const buildUtmCampaign = (utm: Utm | undefined) => {
  return utm ? utm.campaign : 'angel-web-project'
}

export const buildMerchDiscountUrl = (product: ShopifyProduct, discount: MerchDiscount) => {
  try {
    const parsedUrl = new URL(product?.onlineStoreUrl)
    const baseUrl = parsedUrl.origin || paths.external.shop.index
    return `${baseUrl}/discount/${discount.code}?redirect=${parsedUrl.pathname}`
  } catch (error) {
    logger().error('An error occurred creating a merch discount url', error)
    return product?.onlineStoreUrl
  }
}

export const buildGuildMerchUrl = (product: ShopifyProduct) => {
  try {
    const parsedUrl = new URL(product?.onlineStoreUrl)
    return `${paths.guild.merchandise}/${parsedUrl.pathname}`
  } catch (error) {
    logger().error('An error occurred creating a guild merch discount url', error)
    return product?.onlineStoreUrl
  }
}

export const buildProductUrl = (product: ShopifyProduct, hasMerchDiscount: boolean, discount?: MerchDiscount) => {
  const onlineStoreUrl = product?.onlineStoreUrl

  if (hasMerchDiscount && onlineStoreUrl?.startsWith(paths.external.shop.index)) return buildGuildMerchUrl(product)
  if (discount) return buildMerchDiscountUrl(product, discount)

  return onlineStoreUrl
}
